import * as Sentry from '@sentry/react';
import { reactRouterBrowserTracingIntegration } from '@sentry/react';
import { useEffect } from 'react';
import { RootOptions } from 'react-dom/client';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router';

import { APP_MODE, IS_DEVELOPMENT } from '@src/constants';
import {
  isUrlFromBlockedDomain,
  sanitizeSensitiveData,
} from '@src/helpers/sentry.helpers';

Sentry.init({
  environment: APP_MODE,
  dsn: import.meta.env.VITE_SENTRY_AUTH_DSN,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.breadcrumbsIntegration({
      console: true,
      dom: true,
      fetch: true,
      history: true,
      xhr: true,
    }),
    reactRouterBrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
  ],
  beforeBreadcrumb: (breadcrumb, hint) => {
    if (isUrlFromBlockedDomain(breadcrumb, hint)) return null;

    const xhr = hint?.xhr;

    const updatedBreadcrumb = {
      ...breadcrumb,
      data: {
        ...breadcrumb.data,
        currentUrl: window.location.href,
        ...(xhr && {
          requestBody: xhr.__sentry_xhr_v3__?.body,
          response: xhr.response,
          responseType: xhr.responseType,
          responseUrl: xhr.responseURL,
          status: xhr.status,
        }),
      },
    };

    return sanitizeSensitiveData(updatedBreadcrumb);
  },
  tracesSampleRate: IS_DEVELOPMENT ? 1.0 : 0.1, //  Capture 100% of the transactions
  tracePropagationTargets: ['localhost', import.meta.env.VITE_APP_BASE_URL_API],
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  ignoreErrors: [
    'Network Error',
    'Request failed with status code 400',
    /Error invoking .*: Java object is gone/i,
  ],
});

export const sentryErrorsHandlersOptions: RootOptions = {
  onRecoverableError: Sentry.reactErrorHandler(),
};
